<template>
    <b-card class="formations directors">
        <b-card title="Directors" class="mt-2 bordered-card directors-section">
            <director-index
                v-if="! showCreateOrEditDirectorForm && ! showCreateOrEditNomineeDirectorForm"
                ref="createOrEditDirector"
                :directors-list="{
                    directors,
                    directorsDropdownList,
                    nomineeDirectors,
                    enstonDirectorsForDropdown,
                    hasNomineeDirector,
                }"
                :director-blue-print="directorBluePrint"
                :is-fetching-data="isFetchingData"
                @createDirector="openCreateOrEditDirectorForm"
                @edit="editDirector"
                @remove="removeDirector"
                @openCreateDirectorForm="openCreateOrEditDirectorForm"
                @createDirectorFromDropdown="createDirectorFromDropdown"
                @createNomineeDirector="createNomineeDirector"
            />

            <div class="mt-2 position-relative">
                <!-- create director form -->
                <transition name="fade-bottom" mode="out-in">
                    <create-or-edit-director-form
                        v-if="showCreateOrEditDirectorForm"
                        ref="createOrEditDirector"
                        :data-status="directorStatus"
                        :data-director="directorBluePrint"
                        :data-shareholders="shareholders"
                        @cancel="showCreateOrEditDirectorForm = false"
                        @createOrEdit="storeOrUpdateDirector"
                    />

                    <create-or-edit-nominee-director-form
                        v-if="showCreateOrEditNomineeDirectorForm"
                        ref="createOrEditNomineeDirector"
                        :data-status="nomineeDirectorStatus"
                        :data-nominee-director="nomineeDirectorBluePrint"
                        :data-shareholders="shareholders"
                        @cancel="showCreateOrEditNomineeDirectorForm = false"
                        @createOrEdit="storeOrUpdateNomineeDirector"
                    />
                </transition>
            </div>
        </b-card>

        <div class="d-flex justify-content-between mt-3">
            <b-button variant="outline-primary" @click="$emit('previous')">
                <!-- <img :src="'/icons/left-arrow.svg'" alt="left-arrow" /> -->
                <vector data-type="icons" data-width="16px" data-height="13px" data-name="left-arrow" />
                Previous
            </b-button>

            <b-button
variant="primary" data-test="next"
                :disabled="loading || isFetchingData"
                class=" d-flex justify-content-center next-button align-items-center"
                @click="next"
            >
                Next
                <normal-loader v-if="loading" class="ml-.5" />
                <img v-else :src="'/icons/right-arrow.svg'" class="ml-.5" alt="right-arrow" />
            </b-button>
        </div>
    </b-card>
</template>

<script>
/* eslint-disable no-underscore-dangle */
import { BCard, BButton } from "bootstrap-vue";
import Formation from "@/models/Formation";
import { errorToast } from "@/views/helpers/toast";
import {
    ADD_ENSTON_DIRECTOR_ERROR,
    DIRECTOR_FILL_OUT_ALL_FIELD_ERROR,
    SOMETHING_WENT_WRONG
} from "@/constants/errors";
import { NOMINEE_DIRECTOR, NOMINEE_SHAREHOLDER } from "@/constants/config";
import { formatDate } from '@/helpers';
import { userInfoLocalize } from '@/localize/userInfoLocalizer';
import DirectorIndex from "./directors_and_shareholders/DirectorIndex.vue";
import CreateOrEditDirectorForm from "./directors_and_shareholders/DirectorCreateOrEdit.vue";
import CreateOrEditNomineeDirectorForm from "./directors_and_shareholders/NomineeDirectorCreateOrEdit.vue";

export default {
    components: {
        BCard,
        BButton,
        DirectorIndex,
        CreateOrEditNomineeDirectorForm,
        CreateOrEditDirectorForm,
    },

    data() {
        return {
            directorBluePrint: {
                _id: undefined,
                name: undefined,
                idType: undefined,
                nric: undefined,
                nationality: undefined,
                dateOfExpiry: undefined,
                dob: undefined,
                email: undefined,
                contactNumber: undefined,
                address: undefined,
                // nominee director
                isNomineeDirector: undefined,
                appointmentDate: undefined,
                cessationDate: undefined,
                nominatorType:undefined,
                nominator: undefined,
                nominatorAlias: undefined,
                // new nominator field
                newNominatorName: undefined,
                nominatorNRIC: undefined,
                nominatorIdType: undefined,
                nominatorNationality: undefined,
                nominatorDateOfExpiry: undefined,
                nominatorDOB: undefined,
                nominatorAddress: undefined,
                nominatorEmail: undefined,
                isNewNominator: undefined,
                // end new nominator field
                nominatorUen: undefined,
                nominatorStatue: undefined,
                nominatorLegalForm: undefined,
                nominatorJurisdiction: undefined,
                nominatorRegistrar: undefined,
                remark: undefined,
                generatePOA: undefined,
            },
            directors: [],
            directorsDropdownList: [],
            directorKeys: [],
            directorTotalSteps: 0,
            directorStatus: undefined,
            showCreateOrEditDirectorForm: false,

            nomineeDirectorBluePrint: {
                _id: undefined,
                id: undefined,
                name: undefined,
                cessationDate: undefined,
                appointmentDate: undefined,
                remark: undefined,

                nominatorType: undefined,
                nominator: undefined,
                nominatorAlias: undefined,
                nominatorId: undefined,

                nominatorUen: undefined,
                nominatorStatue: undefined,
                nominatorLegalForm: undefined,
                nominatorJurisdiction: undefined,
                nominatorRegistrar: undefined,
                is_enston_director: undefined,
                is_nominee_director: 1,
                generatePOA: undefined,
            },
            nomineeDirectors: [],
            enstonDirectors: [],
            allNomineeDirectors: [],
            nomineeDirectorStatus: undefined,
            enstonDirectorsForDropdown: [],
            showCreateOrEditNomineeDirectorForm: false,

            shareholders: [],

            meta: {},
            isFetchingData: true,
            loading: false,
        };
    },

    computed: {
        hasNomineeDirector()
        {
            if(this.nomineeDirectors.length > 0) return true;

            return this.directors.filter(nominee => nominee.isNomineeDirector === true).length >= 1;
        },

        hasEnstonDirector()
        {
            return this.nomineeDirectors.filter(user => user.is_enston_director).length >= 1;
        }
    },

    async created() {
        this.isFetchingData = true;
        this.directorKeys = Object.keys(this.directorBluePrint);

        const dataDirectors = await Formation.getDirectors();

        dataDirectors.shareholders.forEach(shareholder => {
            this.shareholders.push({
                referenceId: shareholder.reference_id,
                userId: shareholder.user.id,
                shareholderType: {
                    text: shareholder.shareholder_type,
                    value: shareholder.shareholder_type
                },
                name: shareholder.user.user_info?.legal_name ?? shareholder.user.name,
                email: shareholder.user.email,
                nric: shareholder.user.user_info.NRIC,
            });
        });
        
        dataDirectors.directors.forEach(director => {
            this.directors.push({
                _id: this.generateUniqueId(),
                name: director.user.user_info?.legal_name ?? director.user.name,
                idType: director.user.user_info?.id_type
                    ? {
                        id: director.user.user_info.id_type?.id,
                        name: director.user.user_info.id_type?.name,
                    }
                    : undefined,
                nric: director.user.user_info
                    ? director.user.user_info.NRIC
                    : undefined,
                nationality: director.user.user_info?.nationality
                    ? { 
                        value: director.user.user_info.nationality?.id, 
                        text: director.user.user_info.nationality?.name 
                    }
                    : undefined,
                dateOfExpiry: formatDate(director.user.user_info?.date_of_expiry),
                dob: director.user.user_info
                    ? formatDate(director.user.user_info.DOB)
                    : undefined,
                email: director.user.email,
                contactNumber: director.user.user_info
                    ? director.user.user_info.contact_number
                    : undefined,
                address: director.user.user_info
                    ? director.user.user_info.address
                    : undefined,
                
                // nominee director fields
                appointmentDate: director?.appointment_date ? formatDate(director.appointment_date) : null,
                cessationDate: director?.cessation_date ? formatDate(director.cessation_date) : null,
                nominatorType: director?.shareholder_type ?
                    {
                        text: director.shareholder_type.includes('Individual') ? NOMINEE_DIRECTOR.NOMINATOR.TYPE.INDIVIDUAL : NOMINEE_DIRECTOR.NOMINATOR.TYPE.CORPORATE,
                        value: director.shareholder_type.includes('Individual') ? NOMINEE_DIRECTOR.NOMINATOR.TYPE.INDIVIDUAL : NOMINEE_DIRECTOR.NOMINATOR.TYPE.CORPORATE,
                    }
                    : undefined,
                nominator: director?.shareholder?.user ? 
                    {
                        // _idForShareholder: this.shareholders.find(shareholder => shareholder.referenceId === director?.shareholder?.user?.id)?._id,
                        referenceId: this.shareholders.find(shareholder => shareholder.referenceId === director.shareholder?.user?.id)?.referenceId,
                        userId: director.shareholder.user.id,
                        name: director.shareholder.user.user_info.legal_name,
                        email: director.shareholder.user.email,
                        nric: director.shareholder.user.user_info.NRIC,
                    }
                    : undefined,
                nominatorAlias: director?.shareholder?.alias,
                nominatorEmail: director?.shareholder?.user?.email,
                nominatorUen: director?.shareholder?.uen,
                nominatorStatue: director?.shareholder?.statue ? 
                    {
                        id: director.shareholder.statue.id,
                        name: director.shareholder.statue.name,
                    }
                    : null,
                nominatorLegalForm: director?.shareholder?.legal_form,
                nominatorJurisdiction: director?.shareholder?.jurisdiction ?
                    {
                        id: director.shareholder.jurisdiction.id,
                        name: director.shareholder.jurisdiction.name,
                    }
                    : null,
                nominatorRegistrar: director?.shareholder?.registrar ? 
                    {
                        id: director.shareholder.registrar.id,
                        name: director.shareholder.registrar.name,
                    }
                    : null,
                remark: director.remark,
                isNomineeDirector: false,
                isNewNominator: false,
                generatePOA: false,
            });
        });

        dataDirectors.company_nominee_directors.forEach(director => {
            // enston director
            if(director.user.is_nominee_director) {
                this.nomineeDirectors.push({
                    _id: this.generateUniqueId(),
                    id: director.user.id,
                    name: director.user.user_info?.legal_name ?? director.user.name,
                    cessationDate: director.cessation_date,
                    appointmentDate: director.appointment_date,
                    remark: director.remark,

                    nominatorType: {
                        text: director.shareholder_type.includes('Individual') ? NOMINEE_DIRECTOR.NOMINATOR.TYPE.INDIVIDUAL : NOMINEE_DIRECTOR.NOMINATOR.TYPE.CORPORATE,
                        value: director.shareholder_type.includes('Individual') ? NOMINEE_DIRECTOR.NOMINATOR.TYPE.INDIVIDUAL : NOMINEE_DIRECTOR.NOMINATOR.TYPE.CORPORATE,
                    },
                    nominator: {
                        referenceId: this.shareholders.find(shareholder => shareholder.referenceId === director.shareholder?.user?.id)?.referenceId,
                        userId: director.shareholder.user ? director.shareholder.user.id : undefined,
                        name: director.shareholder.user ? director.shareholder.user.user_info.legal_name : undefined,
                        email: director.shareholder.user ? director.shareholder.user.email : undefined,
                        nric: director.shareholder.user.user_info ? director.shareholder.user.user_info.NRIC : undefined,
                    },
                    nominatorAlias: director.shareholder.alias,

                    nominatorUen: director.shareholder.uen,
                    nominatorStatue: {
                        id: director.shareholder.statue ? director.shareholder.statue.id : undefined,
                        name: director.shareholder.statue ? director.shareholder.statue.name : undefined,
                    },
                    nominatorLegalForm: director.shareholder.legal_form,
                    nominatorJurisdiction: {
                        id: director.shareholder.jurisdiction ? director.shareholder.jurisdiction.id : undefined,
                        name: director.shareholder.jurisdiction ? director.shareholder.jurisdiction.name : undefined,
                    },
                    nominatorRegistrar: {
                        id: director.shareholder.registrar ? director.shareholder.registrar.id : undefined,
                        name: director.shareholder.registrar ? director.shareholder.registrar.name : undefined,
                    },
                    generatePOA: director.generate_poa === 1,
                    is_enston_director: director?.user.is_nominee_director,
                    is_nominee_director: 1
                });
            }
            else {
                // not enston director but nominee director
                const nominatorType = director?.shareholder_type.includes('Individual') ? NOMINEE_DIRECTOR.NOMINATOR.TYPE.INDIVIDUAL : NOMINEE_DIRECTOR.NOMINATOR.TYPE.CORPORATE;
                
                this.directors.push({
                    _id: this.generateUniqueId(),
                    name: director.user.user_info.legal_name,
                    idType: director.user.user_info?.id_type
                        ? {
                            id: director.user.user_info.id_type?.id,
                            name: director.user.user_info.id_type?.name,
                        }
                        : undefined,
                    nric: director.user.user_info
                        ? director.user.user_info.NRIC
                        : undefined,
                    nationality: director.user.user_info?.nationality
                        ? { 
                            value: director.user.user_info.nationality?.id, 
                            text: director.user.user_info.nationality?.name 
                        }
                        : undefined,
                    dateOfExpiry: formatDate(director.user.user_info?.date_of_expiry),
                    dob: director.user.user_info
                        ? formatDate(director.user.user_info.DOB)
                        : undefined,
                    email: director.user.email,
                    contactNumber: director.user.user_info
                        ? director.user.user_info.contact_number
                        : undefined,
                    address: director.user.user_info
                        ? director.user.user_info.address
                        : undefined,
                
                    // nominee director fields
                    appointmentDate: formatDate(director?.appointment_date),
                    cessationDate: director?.cessation_date ? formatDate(director.cessation_date) : null,
                    nominatorType: director?.shareholder_type ?
                        {
                            text: nominatorType,
                            value: nominatorType,
                        }
                        : undefined,
                    nominatorNRIC: nominatorType === NOMINEE_DIRECTOR.NOMINATOR.TYPE.INDIVIDUAL ? director?.shareholder?.user?.user_info?.NRIC : director?.shareholder?.corporate_user?.registration_number,
                    nominator: director?.shareholder?.user ? 
                        {
                            // _idForShareholder: this.shareholders.find(shareholder => shareholder.referenceId === director?.shareholder?.user?.id)?._id,
                            referenceId: this.shareholders.find(shareholder => shareholder.referenceId === director.shareholder?.user?.id)?.referenceId,
                            userId: director.shareholder.user.id,
                            name: director.shareholder.user?.user_info?.legal_name ?? director.shareholder.user.name,
                            email: director.shareholder.user.email,
                            nric: director.shareholder.user.user_info?.NRIC,
                        }
                        : undefined,
                    newNominatorName: director?.shareholder.user?.user_info?.legal_name ?? director?.shareholder.user.name,
                    nominatorAlias: director?.shareholder?.alias,
                    nominatorEmail: director?.shareholder?.user?.email,
                    nominatorDOB: director?.shareholder?.user?.user_info?.DOB ? formatDate(director.shareholder.user.user_info.DOB) : null,
                    nominatorIdType:  director?.shareholder?.user?.user_info?.id_type ?
                        {
                            id: director.shareholder.user.user_info?.id_type.id,
                            name: director.shareholder.user.user_info?.id_type.name
                        }
                        : undefined,
                    nominatorNationality: director?.shareholder?.user?.user_info?.nationality ?
                        {
                            value: director.shareholder.user.user_info.nationality.id,
                            text: director.shareholder.user.user_info.nationality.name,
                        }
                        : undefined,
                    nominatorDateOfExpiry: formatDate(director.user?.user_info?.date_of_expiry),
                    nominatorAddress: nominatorType === NOMINEE_DIRECTOR.NOMINATOR.TYPE.INDIVIDUAL ? director?.shareholder?.user?.user_info?.address : director?.shareholder?.corporate_user?.address,
                    nominatorUen: director?.shareholder?.uen,
                    nominatorStatue: director?.shareholder?.statue ? 
                        {
                            id: director.shareholder.statue.id,
                            name: director.shareholder.statue.name,
                        }
                        : null,
                    nominatorLegalForm: director?.shareholder?.legal_form,
                    nominatorJurisdiction: director?.shareholder?.jurisdiction ?
                        {
                            id: director.shareholder.jurisdiction.id,
                            name: director.shareholder.jurisdiction.name,
                        }
                        : null,
                    nominatorRegistrar: director?.shareholder?.registrar ? 
                        {
                            id: director.shareholder.registrar.id,
                            name: director.shareholder.registrar.name,
                        }
                        : null,
                    remark: director.remark,
                    generatePOA: director.generate_poa === 1,
                    isNomineeDirector: true,
                    isNewNominator: this.notFromShareholderList(director?.shareholder?.user?.email),
                });
            }
        });

        // the list from the dropdown
        dataDirectors.users.forEach(user => {
            // director.name !== user.name &&
            if (
                this.directors.find(
                    director =>
                        director.email !== user.email
                )
            ) {
                this.directorsDropdownList.push(userInfoLocalize(user));
            }
        });

        this.enstonDirectors = dataDirectors.enston_directors;

        this.allNomineeDirectors = dataDirectors.enston_directors;

        const enstonDirector = this.nomineeDirectors.filter(ed => ed.is_enston_director);

        if(enstonDirector.length < 1)
            this.initializeEnstonDirectorsForDropdown();

        this.meta = dataDirectors.meta;
        // eslint-disable-next-line no-unneeded-ternary
        this.isFetchingData = false;
    },

    methods: {
        editDirector(director)
        {
            if (director.is_nominee_director)
                this.openCreateOrEditNomineeDirectorForm(director, 'edit');
            else 
                this.openCreateOrEditDirectorForm(director, 'edit');
        },

        // directors
        createDirectorFromDropdown(director) {
            this.openCreateOrEditDirectorForm(director);
        },

        openCreateOrEditDirectorForm(
            director = this.directorBluePrint,
            status = "create"
        ) {
            // reset Blue Print data
            Object.keys(this.directorBluePrint).forEach(key => {
                this.directorBluePrint[key] = director[key] ?? null;
            });

            this.showCreateOrEditDirectorForm = true;
            this.directorStatus = status;
        },

        initializeEnstonDirectorsForDropdown()
        {
            this.enstonDirectors.forEach(user => {
                this.enstonDirectorsForDropdown.push({
                    id: user.id,
                    name: user?.user_info?.legal_name ?? user.name,
                    email: user.email,
                    is_enston_director: user.is_nominee_director,
                    is_nominee_director: 1
                });
            });
        },

        storeOrUpdateDirector(director) {
            if (this.directorStatus === "edit") {
                this.directors.forEach(existingDirector => {
                    if (existingDirector._id === director._id) {
                        Object.keys(this.directorBluePrint).forEach(key => {
                            // eslint-disable-next-line no-param-reassign
                            existingDirector[key] = director[key];
                        });
                    }
                });
            }

            if (this.directorStatus === "create") {
                // eslint-disable-next-line no-param-reassign
                director._id = this.generateUniqueId();
                this.directors.push(director);
            }

            this.showCreateOrEditDirectorForm = false;

            // dropdown director and shareholder features
            this.directorsDropdownList = this.directorsDropdownList.filter(
                user => user.name !== director.name && user.email && director.email
            );
        },

        removeDirector(director) {
            if (director.is_nominee_director)
            {
                if(director.is_enston_director)
                    this.initializeEnstonDirectorsForDropdown();
                    
                this.nomineeDirectors = this.nomineeDirectors.filter(
                    existingDirector => existingDirector._id !== director._id
                );
            } else {
                this.directors = this.directors.filter(
                    existingDirector => existingDirector._id !== director._id
                );
                this.directorsDropdownList.push(director);
            }
        },

        // nominee directors
        createNomineeDirector(nomineeDirector) {
            // eslint-disable-next-line no-param-reassign
            this.showCreateOrEditNomineeDirectorForm = true;
            this.openCreateOrEditNomineeDirectorForm(nomineeDirector);
        },

        openCreateOrEditNomineeDirectorForm(
            nomineeDirector = this.nomineeDirectorBluePrint,
            status = "create"
        ) {
            // reset Blue Print data
            Object.keys(this.nomineeDirectorBluePrint).forEach(key => {
                this.nomineeDirectorBluePrint[key] = nomineeDirector[key] ?? null;
            });

            this.showCreateOrEditNomineeDirectorForm = true;
            this.nomineeDirectorStatus = status;
        },

        storeOrUpdateNomineeDirector(nomineeDirector) {
            if (this.nomineeDirectorStatus === "edit") {
                this.nomineeDirectors.forEach(existingNomineeDirector => {
                    if (existingNomineeDirector._id === nomineeDirector._id) {
                        Object.keys(this.nomineeDirectorBluePrint).forEach(
                            key => {
                                // eslint-disable-next-line no-param-reassign
                                existingNomineeDirector[key] = nomineeDirector[key];
                            }
                        );
                    }
                });
            }

            if (this.nomineeDirectorStatus === "create") {
                // eslint-disable-next-line no-param-reassign
                nomineeDirector._id = this.generateUniqueId();
                this.nomineeDirectors.push(nomineeDirector);
            }

            this.enstonDirectorsForDropdown = [];
            this.showCreateOrEditNomineeDirectorForm = false;
        },

        notFromShareholderList(nominatorEmail) {
            return this.shareholders.filter(shareholder => nominatorEmail === shareholder.email).length < 1;
        },

        generateUniqueId() {
            return Math.round(Math.random() * 1000);
        },

        next() {
            this.loading = true;
            if (! this.validate(this.directors)) {
                errorToast(DIRECTOR_FILL_OUT_ALL_FIELD_ERROR);
                this.loading = false;
                return;
            }

            if (
                this.meta.is_nominee_director_fee_brought &&
                ! this.hasEnstonDirector
            ) {
                errorToast(ADD_ENSTON_DIRECTOR_ERROR);
                this.loading = false;
                return;
            }

            Formation.updateDirectors({
                directors: this.directors,
                enstonDirectors: this.nomineeDirectors
            })
                .then(() => {
                    this.$emit("next");
                    this.loading = false;
                })
                .catch(e => {
                    errorToast(SOMETHING_WENT_WRONG);
                    console.error(
                        "error while trying to store directors in formation",
                        e
                    );
                    this.loading = false;
                });
        },

        validate(directors) {
            const validDirectorCount = directors.filter(
                director => Object.keys(director).filter(key => {
                    // director is nominee director and new nominator
                    if(director.isNomineeDirector && director.isNewNominator) {
                        if(director.nominatorType.text === NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.INDIVIDUAL) {
                            return (
                                !!director[key] &&
                                !key.includes('isNomineeDirector') &&
                                !key.includes('dateOfExpiry') &&
                                !key.includes('cessationDate') &&
                                !key.includes('nominatorUen') &&
                                !key.includes('nominatorLegalForm') &&
                                !key.includes('nominatorStatue') &&
                                !key.includes('nominatorRegistrar') &&
                                !key.includes('nominatorJurisdiction') &&
                                !key.includes('nominatorAlias') &&
                                !key.includes('remark') &&
                                !key.includes('generatePOA') &&
                                // ignore new nominator field
                                !key.includes('isNewNominator') &&
                                !key.includes('nominatorEmail') &&
                                !key.includes('nominatorDateOfExpiry') &&
                                key !== 'nominator'
                            );
                        }

                        return (
                            !!director[key] &&
                            !key.includes('isNomineeDirector') &&
                            !key.includes('dateOfExpiry') &&
                            !key.includes('nominatorAlias') &&
                            !key.includes('cessationDate') &&
                            !key.includes('remark') &&
                            !key.includes('generatePOA') &&
                            // ignore new nominator field
                            !key.includes('isNewNominator') &&
                            !key.includes('nominatorEmail') &&
                            !key.includes('nominatorDOB') &&
                            !key.includes('nominatorIdType') &&
                            !key.includes('nominatorNationality') &&
                            !key.includes('nominatorDateOfExpiry') &&
                            key !== 'nominator'
                        );
                    }

                    // director is nominee director but not new nominator
                    if(director.isNomineeDirector && ! director.isNewNominator) {
                        if(director.nominatorType.text === NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.INDIVIDUAL) {
                            return (
                                !!director[key] &&
                                !key.includes('isNomineeDirector') &&
                                !key.includes('dateOfExpiry') &&
                                !key.includes('cessationDate') &&
                                !key.includes('nominatorUen') &&
                                !key.includes('nominatorLegalForm') &&
                                !key.includes('nominatorStatue') &&
                                !key.includes('nominatorRegistrar') &&
                                !key.includes('nominatorJurisdiction') &&
                                !key.includes('nominatorAlias') &&
                                !key.includes('remark') &&
                                !key.includes('generatePOA') &&
                                // new nominator fields
                                !key.includes('isNewNominator') &&
                                !key.includes('nominatorNRIC') &&
                                !key.includes('nominatorDOB') &&
                                !key.includes('nominatorAddress') &&
                                !key.includes('nominatorIdType') &&
                                !key.includes('nominatorNationality') &&
                                !key.includes('nominatorDateOfExpiry') &&
                                !key.includes('newNominatorName') &&
                                !key.includes('nominatorEmail')
                            );
                        }

                        return (
                            !!director[key] &&
                            !key.includes('isNomineeDirector') &&
                            !key.includes('dateOfExpiry') &&
                            !key.includes('nominatorAlias') &&
                            !key.includes('cessationDate') &&
                            !key.includes('remark') &&
                            !key.includes('generatePOA') &&
                            // new nominator fields
                            !key.includes('isNewNominator') &&
                            !key.includes('nominatorNRIC') &&
                            !key.includes('nominatorDOB') &&
                            !key.includes('nominatorAddress') &&
                            !key.includes('nominatorIdType') &&
                            !key.includes('nominatorNationality') &&
                            !key.includes('nominatorDateOfExpiry') &&
                            !key.includes('newNominatorName') &&
                            !key.includes('nominatorEmail')
                        );
                    }

                    return (
                        !!director[key] &&
                        !key.includes('nominatorType') &&
                        !key.includes('isNomineeDirector') &&
                        !key.includes('dateOfExpiry') &&
                        !key.includes('nominatorAlias') &&
                        !key.includes('appointmentDate') &&
                        !key.includes('cessationDate') &&
                        !key.includes('nominatorUen') &&
                        !key.includes('nominatorLegalForm') &&
                        !key.includes('nominatorStatue') &&
                        !key.includes('nominatorRegistrar') &&
                        !key.includes('nominatorJurisdiction') &&
                        !key.includes('remark') &&
                        !key.includes('generatePOA') &&
                        key !== 'nominator' &&
                        // new nominator fields
                        !key.includes('isNewNominator') &&
                        !key.includes('nominatorNRIC') &&
                        !key.includes('nominatorDOB') &&
                        !key.includes('nominatorAddress') &&
                        !key.includes('nominatorIdType') &&
                        !key.includes('nominatorNationality') &&
                        !key.includes('nominatorDateOfExpiry') &&
                        !key.includes('newNominatorName') &&
                        !key.includes('nominatorEmail')
                    );
                }).length === this.perfectDirectorLength(director)
            ).length;

          
            
            return validDirectorCount === directors.length;
        },

        perfectDirectorLength(director) {
            // director with new nominator
            if(director.isNomineeDirector && director.isNewNominator) {
                // ignore, remark, cessation, isNomineeDirector, alias, and corporate fields
                // ignore 3 new nominator fields(isNewNominator, nominatorEmail, nominator)
                // ignore generatePOA
                // ignore dateOfExpiry and nominatorDateOfExpiry
                if(director.nominatorType?.text === NOMINEE_DIRECTOR.NOMINATOR.TYPE.INDIVIDUAL)
                    return Object.keys(this.directorBluePrint).length - (1 + 1 + 1 + 1 + 5 + 3 + 1 + 2);

                // ignore, remark, alias, cessation and isNomineeDirector
                // ignore 6 new nominator fields(isNewNominator, nominatorEmail, nominator, nominatorDOB, nominatorIdType, nominatorNationality) 
                // ignore generatePOA
                // ignore dateOfExpiry and nominatorDateOfExpiry
                return Object.keys(this.directorBluePrint).length - (1 + 1 + 1 + 1 + 6 + 1 + 2);
            }

            // director with existing nominator
            if(director.isNomineeDirector && !director.isNewNominator) {
                // ignore, remark, cessation, isNomineeDirector, alias, and corporate fields, 8 new nominator fields
                // ignore generatePOA
                // ignore dateOfExpiry and nominatorDateOfExpiry
                if(director.nominatorType?.text === NOMINEE_DIRECTOR.NOMINATOR.TYPE.INDIVIDUAL)
                    return Object.keys(this.directorBluePrint).length - (1 + 1 + 1 + 1 + 5 + 8 + 1 + 2);

                // ignore, remark, alias, cessation and isNomineeDirector, 8 new nominator fields
                // ignore generatePOA
                // ignore dateOfExpiry and nominatorDateOfExpiry
                return Object.keys(this.directorBluePrint).length - (1 + 1 + 1 + 1 + 8 + 1 + 2);
            }
                
            // ignore , remark, cessation, appointment, uen, legal form, jurisdiction, statue, registrar
            // nominator name, alias, isNomineeDirector, nominator type, 8 new nominator fields
            // ignore generatePOA
            // ignore dateOfExpiry and nominatorDateOfExpiry
            return Object.keys(this.directorBluePrint).length - (12 + 8 + 1 + 2);
        },
    },
};
</script>

<style>
.formations.directors .card-body {
    padding: 1.3rem;
}
</style>
